import {Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {AnalyticsDefinition, AnalyticsSummaryItem, VisionContext, VisionPolicy} from '../../models/vte';
import {OperationsOrganizationService} from './operations-organization.service';
import {VisionConfigService} from './vision-config.service';
import {
    ensureExactlyOneVehicleDetectionCapability,
    filterExclusiveVehicleDetectionDefinitions,
    isVehicleDetectionAnalyticsKey,
    normalizeVehicleDetectionCapabilities,
    resolveVisionModeForCapabilities,
    toggleVehicleDetectionCapabilities,
} from './vision-customer-labels';
import {DEFAULT_VISION_POLICY} from './vte.constants';
import {VteApiService} from './vte-api.service';

export type OrganizationVisionSettingsState = {
    organizationId: number;
    policy: VisionPolicy;
    context: VisionContext;
    availableDefinitions: AnalyticsDefinition[];
    /** OV-A3: read-only camera aggregate from Vision Policy GET. */
    analyticsSummary: AnalyticsSummaryItem[];
    featureFlags: Record<string, boolean>;
};

export type OrganizationVisionSettingsForm = {
    vision_mode: VisionPolicy['vision_mode'];
    vision_capabilities: string[];
    vision_provider: string;
    enforce_threshold: number;
    vision_failure_policy: VisionPolicy['vision_failure_policy'];
    vision_timeout_seconds: number;
};

export type VisionModeOption = {
    value: VisionPolicy['vision_mode'];
    label: string;
    hint: string;
};

@Injectable({
    providedIn: 'root',
})
export class OrganizationVisionSettingsViewService {
    readonly visionModeOptions: VisionModeOption[] = [
        {
            value: 'disabled',
            label: 'Disabled (Automatic)',
            hint: 'Automatic when no organization-level Vision capabilities are enabled.',
        },
        {
            value: 'monitor_only',
            label: 'Monitor Only (Automatic)',
            hint: 'Automatically enabled when organization-level Vision capabilities become active.',
        },
        {
            value: 'enforced',
            label: 'Enforced (Manual)',
            hint: 'Manual administrator decision.',
        },
    ];

    readonly failurePolicyOptions = [
        {value: 'fail_open' as const, label: 'Fail open'},
        {value: 'fail_closed' as const, label: 'Fail closed'},
    ];

    constructor(
        private vteApi: VteApiService,
        private visionConfig: VisionConfigService,
        private organizationService: OperationsOrganizationService,
    ) {
    }

    canFilterByOrganization(): boolean {
        return this.organizationService.canFilterByOrganization();
    }

    loadOrganizationOptions() {
        return this.organizationService.loadOrganizationOptions();
    }

    resolveOrganizationId(selectedOrgId?: string | null): number | null {
        if (this.canFilterByOrganization() && selectedOrgId?.trim()) {
            const parsed = Number(selectedOrgId);
            return Number.isNaN(parsed) ? null : parsed;
        }
        return this.organizationService.getCurrentOrganizationId();
    }

    loadSettings(organizationId: number): Observable<OrganizationVisionSettingsState> {
        return forkJoin({
            policy: this.vteApi.getVisionPolicy(organizationId).pipe(
                catchError(() => of({...DEFAULT_VISION_POLICY})),
            ),
            context: this.visionConfig.loadForOrganization(organizationId, true),
        }).pipe(
            map(({policy, context}) => {
                const normalizedPolicy: VisionPolicy = {
                    ...DEFAULT_VISION_POLICY,
                    ...policy,
                    vision_capabilities: [...(policy.vision_capabilities || [])],
                    analytics_summary: this.normalizeAnalyticsSummary(policy.analytics_summary),
                };
                return {
                    organizationId,
                    policy: normalizedPolicy,
                    context,
                    availableDefinitions: this.getAvailableDefinitions(context),
                    analyticsSummary: normalizedPolicy.analytics_summary || [],
                    featureFlags: this.extractFeatureFlags(context),
                };
            }),
        );
    }

    toForm(policy: VisionPolicy): OrganizationVisionSettingsForm {
        return {
            vision_mode: policy.vision_mode || 'disabled',
            vision_capabilities: ensureExactlyOneVehicleDetectionCapability(
                normalizeVehicleDetectionCapabilities([...(policy.vision_capabilities || [])]),
            ),
            vision_provider: policy.vision_provider || '',
            enforce_threshold: policy.enforce_threshold ?? DEFAULT_VISION_POLICY.enforce_threshold,
            vision_failure_policy: policy.vision_failure_policy || DEFAULT_VISION_POLICY.vision_failure_policy,
            vision_timeout_seconds: policy.vision_timeout_seconds ?? DEFAULT_VISION_POLICY.vision_timeout_seconds,
        };
    }

    /**
     * Vision Mode & Policies PATCH payload only.
     * Never includes analytics_summary (read-only camera aggregate).
     */
    toPatchPayload(
        form: OrganizationVisionSettingsForm,
        availableDefinitions: AnalyticsDefinition[] = [],
    ): Partial<VisionPolicy> {
        const capabilities = ensureExactlyOneVehicleDetectionCapability(
            normalizeVehicleDetectionCapabilities([...form.vision_capabilities]),
        );
        const resolved = resolveVisionModeForCapabilities(
            capabilities,
            form.vision_mode,
            true,
        );
        return {
            vision_mode: resolved.mode,
            vision_capabilities: capabilities,
            enforce_threshold: form.enforce_threshold,
            vision_failure_policy: form.vision_failure_policy,
            vision_timeout_seconds: form.vision_timeout_seconds,
        };
    }

    saveSettings(
        organizationId: number,
        form: OrganizationVisionSettingsForm,
        availableDefinitions: AnalyticsDefinition[] = [],
    ): Observable<VisionPolicy> {
        return this.vteApi.patchVisionPolicy(
            organizationId,
            this.toPatchPayload(form, availableDefinitions),
        );
    }

    normalizeAnalyticsSummary(
        summary: AnalyticsSummaryItem[] | null | undefined,
    ): AnalyticsSummaryItem[] {
        if (!Array.isArray(summary)) {
            return [];
        }
        const seen = new Set<string>();
        const items: AnalyticsSummaryItem[] = [];
        for (const item of summary) {
            const key = (item?.analytics_key || '').trim();
            if (!key || seen.has(key)) {
                continue;
            }
            seen.add(key);
            items.push({
                analytics_key: key,
                name: (item.name || key).trim(),
                provider: (item.provider || '').trim(),
            });
        }
        return items;
    }

    getEnabledAnalytics(
        definitions: AnalyticsDefinition[],
        capabilities: string[],
    ): AnalyticsDefinition[] {
        const capabilitySet = new Set(normalizeVehicleDetectionCapabilities(capabilities));
        const enabled = definitions.filter(
            (definition) => definition.is_active && capabilitySet.has(definition.analytics_key),
        );
        return filterExclusiveVehicleDetectionDefinitions(enabled);
    }

    /**
     * Vehicle Detection providers are listed distinctly and are mutually exclusive.
     */
    getCustomerFacingDefinitions(
        definitions: AnalyticsDefinition[],
        _capabilities: string[] = [],
    ): AnalyticsDefinition[] {
        return definitions.filter((definition) => definition.is_active);
    }

    isCapabilityEnabledForCustomer(capabilities: string[], analyticsKey: string): boolean {
        return normalizeVehicleDetectionCapabilities(capabilities).includes(analyticsKey);
    }

    toggleCapability(capabilities: string[], analyticsKey: string, enabled: boolean): string[] {
        if (isVehicleDetectionAnalyticsKey(analyticsKey)) {
            return ensureExactlyOneVehicleDetectionCapability(
                toggleVehicleDetectionCapabilities(capabilities, analyticsKey, enabled),
            );
        }
        const next = new Set(capabilities);
        if (enabled) {
            next.add(analyticsKey);
        } else {
            next.delete(analyticsKey);
        }
        return ensureExactlyOneVehicleDetectionCapability([...next]);
    }

    applyVisionModeLifecycle(
        form: OrganizationVisionSettingsForm,
        modeExplicitlySet: boolean,
    ): { form: OrganizationVisionSettingsForm; error: string | null } {
        const capabilities = ensureExactlyOneVehicleDetectionCapability(
            normalizeVehicleDetectionCapabilities([...form.vision_capabilities]),
        );
        const resolved = resolveVisionModeForCapabilities(
            capabilities,
            form.vision_mode,
            modeExplicitlySet,
        );
        return {
            form: {
                ...form,
                vision_capabilities: capabilities,
                vision_mode: resolved.mode,
            },
            error: resolved.error,
        };
    }

    private getAvailableDefinitions(context: VisionContext): AnalyticsDefinition[] {
        return (context.analytics_definitions || []).filter((definition) => definition.is_active);
    }

    private extractFeatureFlags(context: VisionContext): Record<string, boolean> {
        return {
            vte_pipeline_enabled: context.vte_pipeline_enabled !== false,
        };
    }
}
